import React from 'react';
import {
  List,
  Datagrid,
  Create,
  TabbedForm,
  TextInput,
  SelectInput,
  RadioButtonGroupInput,
  ReferenceArrayInput,
  BooleanInput,
  Toolbar,
  ReferenceInput,
  // ReferenceArrayField,
  translate as t,
  Filter,
  LongTextInput,
  FormTab,
  Show, SimpleShowLayout, TextField,
  // RichTextField,
  ChipField,
  Labeled,
  required,
} from 'admin-on-rest';
import {
  FlatButton,
  List as ListMd,
  ListItem,
  Table,
  TableHeader,
  TableRow,
  TableHeaderColumn,
  TableBody,
  TableRowColumn,
  IconButton,
  CardActions,
} from 'material-ui';
import { ListButton, RefreshButton } from 'admin-on-rest/lib/mui/button';
import Refresh from 'material-ui/svg-icons/navigation/refresh';

import Edit from '../components/Edit';
import TranslatableInput from '../components/inputs/TranslatableInput';
import SelectArrayInput from '../components/inputs/SelectArrayInput';
import ProvisionalStateInput from '../components/inputs/ProvisionalStateInput';
import ImageInput from '../components/inputs/ImageInput';
import ProductPriceInput from '../components/inputs/ProductPriceInput';
import PromoteImageButton from '../components/inputs/PromoteImageButton';
import EditButton from '../components/button/EditButton';
import ShowButton from '../components/button/ShowButton';
import ProductSaveButton from '../components/button/ProductSaveButton';
import ProductSorting from '../components/ProductSorting';
import { getTranslationByLocale } from '../utils/TranslationUtils';
import References from '../components/References';
import ProductPriceManagement from '../components/ProductPriceManagement';
import { Section } from '../components/utils/Section';
import TranslatableField from '../components/fields/TranslatableField';
import ThumbnailImageField from '../components/fields/ThumbnailImageField';
import TableArrayField from '../components/fields/TableArrayField';
import LongTextField from '../components/fields/LongTextField';
import CloneButton from '../components/CloneButton';
import IngredientListBuilder from '../components/IngredientListBuilder';
import IngredientListView from '../components/IngredientListView';
import RichTextInput from '../components/inputs/RichTextInput';
import { RichTextField } from '../components/fields/RichTextField';
import { standardTaxCodes } from '../components/standardTaxCodes';
import { VideoField } from '../components/fields/VideoField';

const ProductFilter = (props) => (
  <Filter {...props}>
    <ReferenceInput label="Category" source="category" reference="categories" perPage={9999} alwaysOn>
      <SelectInput optionText={<Title {...props} />} />
    </ReferenceInput>
    <ReferenceInput label="Market" source="market" reference="markets" allowEmpty alwaysOn>
      <SelectInput optionText={<Title {...props} />} />
    </ReferenceInput>
    <TextInput label="Search" source="q" alwaysOn />
  </Filter>
);

export const SortableProductList = t((props) => (
  <List {...props} actions={<IconButton children={<Refresh />} onClick={() => window.location.reload()} />} pagination={null} filter={{ provisional: { $ne: true } }} perPage={10000}>
    <ProductSorting {...props} />
  </List>
));

export const ProductList = t((props) => (
  <List {...props} filter={{ provisional: { $ne: true }, frozen: { $ne: true } }} filters={<ProductFilter />} perPage={25}>
    <Datagrid>
      <TextField source="internalName" />
      <TranslatableField label={props.translate('resources.products.fields.category')} source="category.title" />
      <ShowButton {...props} basePath="/products" property="code" />
      <EditButton {...props} {...props} basePath="/products" property="code" />
    </Datagrid>
  </List>
));

export const ProductShow = t((props) =>
  (
    <Show {...props} title={props.translate('resources.products.productSheet')}>
      <SimpleShowLayout actions>
        <TranslatableField addLabel source="title" />
        <TranslatableField addLabel source="subtitle" />
        <ThumbnailImageField source="mainImageUrl.src" />
        <DownloadButton />
        <ThumbnailImageField source="gifImageUrl.src" />
        <TextField source="internalName" />
        <ChipField source="stations" />
        <References references={[{ name: 'allergens' }, { name: 'ingredients' }]} {...props}>
          <IngredientListView />
        </References>
        <References references={[{ name: 'ingredients' }]} {...props}>
          <TableArrayField
            {...props}
            elStyle={{ marginRight: '5px' }}
            property={{
              headers: [props.translate('resources.products.fields.contents.description'), props.translate('resources.products.fields.contents.supplier'), props.translate('resources.products.fields.contents.supplierOrderId')],
              keys: ['description', 'supplier', 'supplierOrderId'] }}
            source="ingredients"
            populate="servingPlatter"
            label={props.translate('resources.products.fields.servingPlatter')}
          />
        </References>
        <Labeled label={props.translate('resources.products.fields.instructions')}>
          <RichTextField source="instructions" style={{ whiteSpace: 'pre-line' }} />
        </Labeled>
        <VideoField vimeo source="instructionFile.link" />
        <LongTextField source="servingWeight" style={{ whiteSpace: 'pre-line' }} />
        <ChipField source="diets" />

        <ChipField source="allergens" />
        <LongTextField source="listOfContent" style={{ whiteSpace: 'pre-line' }} label={props.translate('resources.products.fields.listOfContent')} />
        <LongTextField source="ingredients" style={{ whiteSpace: 'pre-line' }} />
        {props.permissions && ['superAdmin', 'admin', 'manager', 'editor'].includes(props.permissions.name) ? <TextField source="costPerPortion" /> : <div />}
        <References references={[{ name: 'venues', filter: { products: { $elemMatch: { product: props.match.params.id } } } }]} {...props}>
          <VenueList translate={props.translate} locale={props.locale} />
        </References>
      </SimpleShowLayout>
    </Show>
  )
);

const DownloadButton = t(({ record, translate }) => [
  <FlatButton primary label={translate('resources.products.fields.downloadButton')} href={record && record.mainImageUrl && record.mainImageUrl.src} download />,
  <br />,
  <small>{translate('resources.products.fields.downloadInfo')}</small>,
]);

const ProductTitle = t(({ record, translate }) => (
  <span>{record ? translate('edit.title', { title: record.internalName }) : ''}</span>
));

const Title = t(({ record, locale }) => (getTranslationByLocale(record.title, locale)));

const ProductToolbar = (props) => (
  <Toolbar {...props}>
    <ProductSaveButton {...props} />
  </Toolbar>
);

const mainCategories = [{ id: 'food', name: 'food' }, { id: 'drink', name: 'drink' }];

const Form = t((props) => (
  <TabbedForm {...props} toolbar={<ProductToolbar {...props} />}>
    <FormTab label="Info">
      {
        props.action === 'edit' &&
        props.permissions.superUser &&
        <ProvisionalStateInput {...props} addField source="provisionalData.state" choices={[{ id: 'new', name: 'new' }, { id: 'rejected', name: 'rejected' }, { id: 'accepted', name: 'accepted' }]} />
      }
      {
        props.action === 'edit' &&
        <BooleanInput source="frozen" />
      }
      {
        props.action === 'edit' &&
        <BooleanInput source="private" />
      }
      {
        props.action === 'edit' &&
        <TextInput source="unlockedBy" />
      }
      {
        props.action === 'edit' &&
        props.permissions.superUser &&
        <BooleanInput source="variablePrice" />
      }
      <BooleanInput source="takeAwayEnabled" />
      <BooleanInput source="takeAwayOnly" />
      <BooleanInput source="creperieEnabled" />
      <BooleanInput source="creperieOnly" />
      <BooleanInput source="autoActivation" />
      <LongTextInput source="provisionalData.discussion" />
      <RadioButtonGroupInput source="mainCategory" choices={mainCategories} />
      <ReferenceInput format={(category) => category && category._id} source="category" reference="categories" perPage={9999} allowEmpty>
        <SelectInput optionText={(choice) => getTranslationByLocale(choice.title, props.locale)} options={{ fullWidth: true }} />
      </ReferenceInput>
      <ReferenceInput format={(account) => account && account._id} source="account" reference="accounts" perPage={9999} allowEmpty>
        <SelectInput optionText="name" optionValue="id" options={{ fullWidth: true }} validate={[required]} />
      </ReferenceInput>
      {/* <TagsInput source="articleGroups" /> */}
      <ReferenceArrayInput source="stations" reference="stations" allowEmpty>
        <SelectArrayInput optionText={(choice) => getTranslationByLocale(choice.title, props.locale)} options={{ fullWidth: true }} />
      </ReferenceArrayInput>
      <TextInput
        options={{ fullWidth: true, errorText: props.translate('internalNameHint'), errorStyle: { color: 'darkslategrey' }, floatingLabelFocusStyle: { color: 'darkslategrey' } }}
        source="internalName"
      />
      {/* {props.action === 'create' && <TextInput options={{ fullWidth: true, errorText: props.translate('codeHint'), errorStyle: { color: 'darkslategrey' }, floatingLabelFocusStyle: { color: 'darkslategrey' } }} parse={(value) => value.replace(' ', '-').toLowerCase()} source="code" />} */}
      <TranslatableInput source="title" {...props} />
      <TranslatableInput source="subtitle" {...props} />
      <Section {...props} title="resources.products.fields.productContents">
        {props.action === 'edit' &&
          <References references={[{ name: 'ingredients' }]} {...props}>
            <IngredientListBuilder source="ingredientList" {...props} />
          </References>
        }
        <LongTextInput source="ingredients" label={props.translate('resources.products.fields.ingredients')} />
        <ReferenceArrayInput {...props} source="allergens" reference="allergens" allowEmpty>
          <SelectArrayInput optionText={(choice) => getTranslationByLocale(choice.title, props.locale)} options={{ fullWidth: true }} />
        </ReferenceArrayInput>
        {/* {props.action === 'edit' &&
          <References references={[{ name: 'ingredients' }, { name: 'recipes' }, { name: 'suppliers' }]} {...props}>
            <RecipeBuilder source="contents" {...props} />
          </References>
        } */}
        {/* <ReferenceArrayInput {...props} source="contents" reference="ingredients" allowEmpty>
            <ProductIngredientsSheet />
          </ReferenceArrayInput> */}
        <LongTextInput source="listOfContent" label={props.translate('resources.products.fields.listOfContent')} />
        <ReferenceArrayInput {...props} source="servingPlatter" reference="ingredients" perPage={9999} label={props.translate('resources.products.fields.servingPlatter')} allowEmpty>
          <SelectArrayInput
            optionText="description"
            options={{ fullWidth: true }}
          />
        </ReferenceArrayInput>
        <TextInput source="instructionFile" />
        <RichTextInput source="instructions" label={props.translate('resources.products.fields.instructions')} />
        <LongTextInput source="servingWeight" label={props.translate('resources.products.fields.servingWeight')} />
        <LongTextInput source="costPerPortion" label={props.translate('resources.products.fields.costPerPortion')} />
        <BooleanInput source="alcoholic" />
        <BooleanInput source="readyToDrink" />
        <ReferenceArrayInput {...props} source="diets" reference="diets" allowEmpty>
          <SelectArrayInput optionText={(choice) => getTranslationByLocale(choice.title, props.locale)} options={{ fullWidth: true }} />
        </ReferenceArrayInput>
      </Section>
      {props.permissions.priceManager && (
        <h3>Note: Price editing moved to Price Management tab above and images to Media tab</h3>
      )}
    </FormTab>
    <FormTab label="priceManagement">
      {props.permissions.priceManager && (
        <TextField label="Price suggestion" source="priceSuggestion" />
      )}
      {props.permissions.priceManager && (
        <ReferenceInput source="market" reference="markets" allowEmpty>
          <SelectInput options={{ fullWidth: true }} optionText="countryCode" optionValue="id" />
        </ReferenceInput>
      )}
      {props.permissions.priceManager && (
        <SelectInput source="standardTaxCode" options={{ fullWidth: true }} choices={standardTaxCodes} />
      )}
      {props.permissions.priceManager && (
        <References {...props} references={[{ name: 'markets' }, { name: 'categories', perPage: 9999 }]}>
          <ProductPriceInput source="markets" {...props} />
        </References>
      )}
      <ReferenceArrayInput source="venueWhitelist" reference="venues" perPage={999} allowEmpty>
        <SelectArrayInput options={{ fullWidth: true }} optionText="publicName" />
      </ReferenceArrayInput>
      <References references={[{ name: 'venues', filter: { 'products.product': props.record.code }, perPage: 10000 }]}>
        <ProductPriceManagement {...props} />
      </References>
    </FormTab>
    <FormTab label="media">
      <ImageInput source="gifImageUrl" accept="image/*">
        <ThumbnailImageField source="src" />
      </ImageInput>
      <span style={{ color: 'rgba(0, 0, 0, 0.3)', fontSize: '13px' }}>
        {props.translate('resources.products.fields.provisionalImageUrl')}
      </span>
      <PromoteImageButton source="provisionalImageUrl.src" label="provisionalImageUrl" />
      <ImageInput source="mainImageUrl" accept="image/*">
        <ThumbnailImageField source="src" />
      </ImageInput>
      <ImageInput source="tallImageUrl" accept="image/*">
        <ThumbnailImageField source="src" />
      </ImageInput>
      <ImageInput source="alternativeImageUrl" accept="image/*">
        <ThumbnailImageField source="src" />
      </ImageInput>
      {props.action !== 'create' && <DownloadButton />}
    </FormTab>
  </TabbedForm>
));

const cardActionStyle = {
  zIndex: 2,
  display: 'inline-block',
  float: 'right',
};
const EditActions = ({ data, ...props }) => (
  <CardActions style={cardActionStyle}>
    <CloneButton {...props} record={data} />
    <ShowButton {...props} record={data} />
    <ListButton {...props} record={data} />
    <RefreshButton />
    {/* <ListButton basePath={basePath} /> */}
    {/* <FlatButton primary label="Refresh" onClick={refresh} icon={<Refresh />} /> */}
  </CardActions>
);

export const ProductEdit = (props) => (
  <Edit title={<ProductTitle />} actions={<EditActions {...props} />} {...props}>
    <Form {...props} action="edit" />
  </Edit>
);

export const ProductCreate = (props) => (
  <Create {...props}>
    <Form {...props} action="create" />
  </Create>
);

const VenueList = ({ choices, translate, locale }) => (
  <Labeled label={translate('venues')}>
    <ListMd>
      <ListItem
        // key="activeVenuesListItem"
        primaryText={`${choices.venues.length} ${translate('activeVenues')}`}
        primaryTogglesNestedList
        nestedItems={[
          <Table key="activeVenues">
            <TableHeader
              displaySelectAll={false}
              adjustForCheckbox={false}
            >
              <TableRow>
                <TableHeaderColumn>{translate('venue')}</TableHeaderColumn>
                <TableHeaderColumn>{translate('market')}</TableHeaderColumn>
              </TableRow>
            </TableHeader>
            <TableBody
              displayRowCheckbox={false}
            >
              {
                choices.venues.map((venue) => (
                  <TableRow key={venue.name}>
                    <TableRowColumn>{locale && venue.ModernTitles[locale] ? venue.ModernTitles[locale] : venue.ModernTitles.sv}</TableRowColumn>
                    <TableRowColumn>{venue && venue.market.countryCode}</TableRowColumn>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>,
        ]}
      />
    </ListMd>
  </Labeled>
);
